import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { DeviceHealthQuery } from '../services/device.health.service';

@Component({
  selector: 'health-query-builder',
  templateUrl: './health-query-builder.component.html',
  styleUrls: ['./health-query-builder.component.scss'],
})
export class HealthQueryBuilderComponent implements OnInit {
  @Input() isLoading? = false;
  @Output() search = new EventEmitter();

  serialNumberFilter: string;
  selectedQuery = 'Device';
  companyIdFilter = null;
  invalidDates = false;
  isHealthSearched = false;
  startDateDefault: string = getDefaultDate(
    new Date(new Date().getTime() - 172800000),
  );
  startDateFilter: string = this.startDateDefault;
  endDateDefault: string = getDefaultDate(new Date());
  endDateFilter: string = this.endDateDefault;
  queries = ['Device', 'Install'];
  installationTypes = ['LIA', 'Unknown'];
  query: DeviceHealthQuery = {
    selectedQuery: '',
    serialNumbers: [],
    healthParams: [],
    companyId: null,
    sortOrder: '',
    startDate: null,
    endDate: null,
    size: 20,
  };

  statusProperties: string[] = [
    'Check In',
    'Presence',
    'Ignition',
    'Gps',
    'Accelerometer',
    'Sd Card Health',
    'In Service',
    'Ecm Data',
    'Calibration',
    'Hub Association',
    'Vehicle Association',
    'Vehicle Measurement',
    'LIA',
    'Install Ecm Ignition',
    'Install Ecm Engine Speed',
  ];

  selectedHealthParams: string[] = [];

  constructor(
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParams;
    if (params['buildResult']) {
      const buildResult = JSON.parse(params['buildResult']);
      this.selectedQuery = buildResult.selectedQuery;
      this.serialNumberFilter =
        buildResult.serialNumbers == null ||
        buildResult.serialNumbers.length == 0
          ? null
          : buildResult.serialNumbers[0];
      this.companyIdFilter = buildResult.companyId;
      this.selectedHealthParams = buildResult.healthParams;
      this.startDateFilter = buildResult.startDate;
      this.endDateFilter = buildResult.endDate;
    } else {
      this.selectedHealthParams = [
        'Check In',
        'Presence',
        'Ignition',
        'Gps',
        'Accelerometer',
        'Sd Card Health',
      ];
    }

    this.onSearchClick();
  }

  selectAll() {
    this.selectedHealthParams = this.statusProperties;
  }

  unselectAll() {
    this.selectedHealthParams = [];
  }

  onSearchClick(): void {
    this.isLoading = true;
    this.query.healthParams = this.selectedHealthParams
      ? this.selectedHealthParams
      : [];

    this.query.serialNumbers = this.serialNumberFilter
      ? [this.serialNumberFilter]
      : [];

    this.query.companyId = this.companyIdFilter;

    this.query.sortOrder = 'desc';

    this.query.selectedQuery = this.selectedQuery;

    if (
      this.selectedQuery == 'Device' &&
      (!this.startDateFilter || !this.endDateFilter || this.invalidDates)
    ) {
      this.startDateFilter = this.startDateDefault;
      this.endDateFilter = this.endDateDefault;
    }
    if (this.startDateFilter) {
      this.query.startDate = this.startDateFilter;
    }
    if (this.startDateFilter && this.endDateFilter) {
      if (!this.compareDates(this.startDateFilter, this.endDateFilter)) {
        this.invalidDates = true;
        this.toastr.warning(`End date must be after start date.`);
        return;
      }
      this.query.endDate = this.endDateFilter;
    } else if (
      (this.endDateFilter && !this.startDateFilter) ||
      (this.startDateFilter && !this.endDateFilter)
    ) {
      this.startDateFilter
        ? this.toastr.warning(`An end date is required.`)
        : this.toastr.warning(`A start date is required.`);
      return;
    } else {
      this.query.startDate = null;
      this.query.endDate = null;
    }
    this.isHealthSearched = true;
    this.setStoredParams(this.query);

    this.search.emit(this.query);
  }

  reset() {
    if (this.selectedQuery == 'Install') {
      this.startDateFilter = this.startDateDefault;
      this.endDateFilter = this.endDateDefault;
    }
    this.clearFilters();
  }

  private compareDates(
    startDateFilter: string,
    endDateFilter: string,
  ): boolean {
    const startDateMon = startDateFilter.substring(5, 7);
    const startDateYear = startDateFilter.substring(0, 4);
    const startDateDay = startDateFilter.substring(8, 10);

    const endDateMon = endDateFilter.substring(5, 7);
    const endDateYear = endDateFilter.substring(0, 4);
    const endDateDay = endDateFilter.substring(8, 10);

    if (startDateYear === endDateYear) {
      if (startDateMon < endDateMon) {
        return true;
      } else if (startDateMon == endDateMon) {
        return startDateDay < endDateDay;
      }
    } else if (startDateYear < endDateYear) return true;

    return false;
  }

  private clearFilters(): void {
    this.selectedQuery = 'Device';
    this.serialNumberFilter = '';
    this.companyIdFilter = null;
    this.selectedHealthParams = [
      'Check In',
      'Sd Card Health',
      'Accelerometer',
      'Ignition',
      'Gps',
      'Presence',
    ];
  }

  private setStoredParams(buildResult: DeviceHealthQuery) {
    const result = structuredClone(buildResult);
    result.scrollId = null;
    result.size = null;
    const resultString = JSON.stringify(result);
    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        buildResult: resultString,
      },
    });
  }
}

const getDefaultDate = (date: Date) => {
  const fullYear = date.getFullYear().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${fullYear}-${month}-${day}T${hours}:${minutes}`;
};
